import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { useResourceRecordsContext } from '@/shared/hooks'
import type { ResourceActiveFilterChip } from '@/shared/ui/Layout'

import type { CategoryOption } from '../lib/categoryOptions'

/** Chaves explícitas do filtro server-side — evita params ambíguos no contexto. */
export const PARTNERS_FILTER_KEYS = {
  search: 'name_or_partner_category_name_cont',
  categoryId: 'partner_category_id_eq'
} as const

export type PartnersAdvancedFilters = {
  categoryId: string | null
}

const EMPTY_ADVANCED_FILTERS: PartnersAdvancedFilters = {
  categoryId: null
}

export const usePartnersListFilters = (categoryOptions: CategoryOption[]) => {
  const { params, setFilter } = useResourceRecordsContext() as {
    params: { filter?: Record<string, string> }
    setFilter: (
      value:
        | Record<string, string>
        | ((prev: Record<string, string>) => Record<string, string>)
    ) => void
  }

  const serverSearch = String(
    params.filter?.[PARTNERS_FILTER_KEYS.search] ?? ''
  )
  const serverCategoryId = params.filter?.[PARTNERS_FILTER_KEYS.categoryId]
    ? String(params.filter[PARTNERS_FILTER_KEYS.categoryId])
    : null

  const [searchInput, setSearchInput] = useState(serverSearch)
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    setSearchInput(serverSearch)
  }, [serverSearch])

  const commitSearch = useCallback(
    (value: string) => {
      setFilter(prev => {
        const next = { ...prev }
        const trimmed = value.trim()

        if (trimmed) next[PARTNERS_FILTER_KEYS.search] = trimmed
        else delete next[PARTNERS_FILTER_KEYS.search]

        return next
      })
    },
    [setFilter]
  )

  useEffect(() => {
    searchDebounceRef.current = window.setTimeout(() => {
      if (searchInput === serverSearch) return
      commitSearch(searchInput)
    }, 400)

    return () => {
      if (searchDebounceRef.current) {
        window.clearTimeout(searchDebounceRef.current)
      }
    }
  }, [searchInput, serverSearch, commitSearch])

  const flushSearch = useCallback(() => {
    if (searchDebounceRef.current) {
      window.clearTimeout(searchDebounceRef.current)
      searchDebounceRef.current = null
    }

    if (searchInput === serverSearch) return
    commitSearch(searchInput)
  }, [searchInput, serverSearch, commitSearch])

  const setCategoryId = useCallback(
    (categoryId: string | null) => {
      setFilter(prev => {
        const next = { ...prev }

        if (categoryId) next[PARTNERS_FILTER_KEYS.categoryId] = categoryId
        else delete next[PARTNERS_FILTER_KEYS.categoryId]

        return next
      })
    },
    [setFilter]
  )

  const advancedFilters: PartnersAdvancedFilters = useMemo(
    () => ({ categoryId: serverCategoryId }),
    [serverCategoryId]
  )

  const filterOptions = useMemo(
    () =>
      categoryOptions.map(option => ({
        id: option.value,
        label: option.label
      })),
    [categoryOptions]
  )

  const categoryLabel = categoryOptions.find(
    option => option.value === serverCategoryId
  )?.label

  const activeChips: ResourceActiveFilterChip[] =
    serverCategoryId && categoryLabel
      ? [{ id: 'category', label: `Categoria: ${categoryLabel}` }]
      : []

  return {
    searchInput,
    setSearchInput,
    flushSearch,
    advancedFilters,
    filterOptions,
    hasActiveAdvanced: Boolean(serverCategoryId),
    hasActiveFilters: Boolean(serverSearch.trim() || serverCategoryId),
    setCategoryId,
    resetAdvanced: () => setCategoryId(null),
    applyAdvancedDraft: (draft: PartnersAdvancedFilters) =>
      setCategoryId(draft.categoryId),
    activeChips,
    emptyAdvancedDraft: EMPTY_ADVANCED_FILTERS
  }
}
